import { useEffect, useState } from "react";
import { Loader2, Heart, TrendingUp, UserPlus, UserMinus, Wallet, Gift, AlertTriangle, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import MetaSpendCard from "@/components/admin/MetaSpendCard";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-amtm-textMuted text-xs mb-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-display text-2xl text-amtm-text">{value}</p>
    </div>
  );
}

export default function GuardianOverviewTab() {
  const [data, setData] = useState(null);
  const [diwali, setDiwali] = useState(null);

  useEffect(() => {
    api.get("/guardian/admin/dashboard").then(({ data }) => setData(data));
    api.get("/campaign/diwali/status").then(({ data }) => setDiwali(data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>;

  const progressPct = Math.min(100, (data.active_guardians / data.active_guardian_target) * 100);
  const diwaliPct = diwali ? Math.min(100, (diwali.raised / diwali.final_goal) * 100) : 0;

  return (
    <div data-testid="guardian-overview-tab">
      <MetaSpendCard testId="guardian-meta-spend-card" />
      {diwali && (
        <div className="bg-[#1F0E06] text-white rounded-xl p-5 mb-4" data-testid="diwali-2026-admin-card">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-[#FFD85F]" /> Diwali 2026</span>
            <span className="text-xs text-white/70">{diwali.active ? "Active" : "Not active"}</span>
          </div>
          <p className="font-display text-2xl mb-1" data-testid="diwali-2026-admin-raised">
            ₹{diwali.raised.toLocaleString("en-IN")} <span className="text-sm text-white/70">raised &middot; {diwali.supporters} supporters</span>
          </p>
          <div className="h-2 w-full rounded-full bg-white/15 overflow-hidden mb-1.5">
            <div className="h-full rounded-full bg-[#FFD85F]" style={{ width: `${diwaliPct}%` }} />
          </div>
          <p className="text-xs text-white/70" data-testid="diwali-2026-admin-percentage">
            {diwaliPct.toFixed(1)}% of ₹{diwali.final_goal.toLocaleString("en-IN")}
          </p>
        </div>
      )}

      <div className="bg-amtm-surface border border-amtm-border rounded-xl p-5 mb-4" data-testid="guardian-progress-card">
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2 font-semibold text-amtm-text"><Heart className="h-4 w-4 text-amtm-teal" /> Active Guardians</span>
          <span className="font-display text-xl text-amtm-text" data-testid="guardian-active-count">
            {data.active_guardians} <span className="text-amtm-textMuted text-sm">/ {data.active_guardian_target}</span>
          </span>
        </div>
        <Progress value={progressPct} className="[&>div]:bg-amtm-teal" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp} label="MRR" value={`₹${data.mrr.toLocaleString("en-IN")}`} testId="guardian-stat-mrr" />
        <StatCard icon={UserPlus} label="New Guardians (mo)" value={data.new_guardians_this_month} testId="guardian-stat-new" />
        <StatCard icon={UserMinus} label="Cancellations (mo)" value={data.cancellations_this_month} testId="guardian-stat-cancellations" />
        <StatCard icon={AlertTriangle} label="Churn Rate" value={`${data.churn_rate_percent}%`} testId="guardian-stat-churn" />
        <StatCard icon={Wallet} label="Raised This Month" value={`₹${data.total_raised_this_month.toLocaleString("en-IN")}`} testId="guardian-stat-raised" />
        <StatCard icon={Gift} label="Average Gift" value={`₹${data.average_gift_this_month.toLocaleString("en-IN")}`} testId="guardian-stat-avg-gift" />
        <StatCard icon={AlertTriangle} label="Failed Payments (open)" value={data.failed_payments_open_count} testId="guardian-stat-failed-open" />
      </div>
    </div>
  );
}
